import { Layer } from "bernie-view";
import { UitkDialogAnimationType, UitkFullscreenDialog } from "uitk-react-dialog";
import { ToolbarType, UitkToolbar } from "uitk-react-toolbar";
import { PROPERTY_FILTERS } from "components/flexComponents/PropertyFilters/PropertyFilters";
import { QueryRoute, RouteStore } from "bernie-core";
import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { UitkPrimaryButton, UitkTertiaryButton } from "uitk-react-button";
import { withStores } from "stores";
import { PropertyFiltersGroupProps } from "components/flexComponents/PropertyFilters/typings";
import { PROPERTY_FILTERS_COMPONENT_MAP } from "components/shared/PropertyFIlterComponents/map/PropertyFiltersComponentMap";
import { PROPERTY_FILTERS_COMPONENT_WRAPPER_MAP } from "components/shared/PropertyFIlterComponents/map/PropertyFiltersComponentWrapperMap";
import {
  Action,
  FlexTrackingInfo,
  sendImmediateClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { PropertyFiltersStore } from "src/stores/PropertyFiltersStore";
import { observer } from "mobx-react";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { HotelsStore } from "src/stores/HotelsStore";
import { withImmediatelyFilteringExperiment, ImmediateFilteringProps } from "../ImmediatelyFilteringExperiment";
import { UitkSpacing } from "uitk-react-spacing";

const ClearButtonTracked = FlexClickTracker(UitkTertiaryButton);

interface PropertyFiltersDialogProps extends ImmediateFilteringProps {
  routeStore: RouteStore;
  children: React.ReactNode;
  propertyFilters: PropertyFiltersStore;
  context: ExtendedContextStore;
  hotels: HotelsStore;
}

export const PropertyFiltersDialog = withImmediatelyFilteringExperiment(
  withStores(
    "routeStore",
    "context",
    "hotels",
    "propertyFilters"
  )(
    observer((props: PropertyFiltersDialogProps) => {
      const { routeStore, propertyFilters } = props;
      const l10n = useLocalization();
      const track = useClickTracker();

      const onDismiss = () => {
        propertyFilters.resetFilters();
        routeStore.goBack();
      };

      const onClickClear = () => {
        propertyFilters.resetAllFilters();
      };

      const onSubmit: React.FormEventHandler = (e) => {
        e.preventDefault();

        propertyFilters.defrost();

        const flexTracking: FlexTrackingInfo = {
          moduleName: PROPERTY_FILTERS,
          rfrr: `dialog.done.${propertyFilters.getNumberOfSelectedFilters()}`,
          action: Action.CLICK,
        };

        sendImmediateClickstreamTrackEvent(flexTracking, track);
        if (props.isImmediateFilteringEnabled) routeStore.goBack();
        propertyFilters.applyFilters(props.hotels, props.context, props.isImmediateFilteringEnabled);
      };

      return (
        <QueryRoute query={{ pwaDialog: "filters" }}>
          <Layer id="filters" onDismiss={onDismiss}>
            <UitkFullscreenDialog dialogShow={true} animation={UitkDialogAnimationType.SLIDEUP}>
              <form className="PropertyFiltersDialog uitk-flex uitk-flex-column" onSubmit={onSubmit}>
                <UitkToolbar
                  iconLabel={l10n.formatText("typeahead.cancel")}
                  header={l10n.formatText("propertyfilters.filter")}
                  type={ToolbarType.CLOSE}
                  action={onDismiss}
                  actionContent={
                    <ClearButtonTracked
                      moduleName={PROPERTY_FILTERS}
                      rfrr="dialog.clear"
                      onClick={onClickClear}
                      className="uitk-type-400"
                      text={l10n.formatText("typeahead.templates.clear")}
                    />
                  }
                />
                {props.children}
                <div className=" all-y-padding-two all-x-padding-two">
                  <UitkPrimaryButton type="submit" text={l10n.formatText("datePicker.doneHeader")} isFullWidth={true} />
                </div>
              </form>
            </UitkFullscreenDialog>
          </Layer>
        </QueryRoute>
      );
    })
  )
);

/**
 * Represents a filter group (e.g. Popular Filters) viewed within a Dialog.
 * @param props
 * @constructor
 */
export const PropertyFilterInDialog = (props: PropertyFiltersGroupProps) => {
  const { filter, withoutHeader, headerSize = 500 } = props;

  if (!filter) {
    return null;
  }

  const { name, options } = filter;

  const Component = PROPERTY_FILTERS_COMPONENT_MAP[filter.name];
  const Wrapper = PROPERTY_FILTERS_COMPONENT_WRAPPER_MAP[filter.name] || React.Fragment;

  const headerClassName = `uitk-type-bold uitk-type-${headerSize}`;

  return (
    <fieldset>
      {!withoutHeader && (
        <UitkSpacing padding={{ blockend: "four" }}>
          <legend className={headerClassName}>{filter?.label}</legend>
        </UitkSpacing>
      )}
      <Wrapper>
        {Array.from(options.values()).map((option, index) => (
          <Component
            key={`${option.optionValue}-${index}`}
            option={option}
            filterName={name}
            moduleName="PropertyFilters"
          />
        ))}
      </Wrapper>
    </fieldset>
  );
};
